import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CalendarView from './components/CalendarView';
import Input from '../../components/ui/Input';

import CallLogPanel from './components/CallLogPanel';

import SchedulingAssistant from './components/SchedulingAssistant';
import calendarService from '../../services/calendarService';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import PageLayout from '../../components/ui/PageLayout';
import ContactNotesPanel from './components/ContactNotesPanel';
import EventModal from './components/EventModal';
import QuickNoteModal from './components/QuickNoteModal';

const CalendarScheduling = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('calendar');
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // day, week, month
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Enhanced note-taking states
  const [showQuickNoteModal, setShowQuickNoteModal] = useState(false);
  const [quickNoteDate, setQuickNoteDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotesFilter, setShowNotesFilter] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadCalendarData();
    } else {
      setLoading(false);
      setError('User not authenticated');
    }
  }, [user?.id, selectedDate, viewMode]);

  const loadCalendarData = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Calculate date range based on view mode
      let startDate, endDate;
      if (viewMode === 'day') {
        startDate = format(selectedDate, 'yyyy-MM-dd');
        endDate = format(selectedDate, 'yyyy-MM-dd');
      } else if (viewMode === 'week') {
        startDate = format(startOfWeek(selectedDate), 'yyyy-MM-dd');
        endDate = format(endOfWeek(selectedDate), 'yyyy-MM-dd');
      } else {
        startDate = format(startOfMonth(selectedDate), 'yyyy-MM-dd');
        endDate = format(endOfMonth(selectedDate), 'yyyy-MM-dd');
      }

      const [eventsResult, callLogsResult] = await Promise.all([
        calendarService?.getCalendarEvents?.(user?.id, startDate, endDate) || { data: [], error: null },
        calendarService?.getCallLogs?.(user?.id, 50) || { data: [], error: null }
      ]);

      if (eventsResult?.error) {
        console.error('Error loading events:', eventsResult?.error);
        setError(eventsResult?.error);
      } else {
        setCalendarEvents(eventsResult?.data || []);
      }

      if (callLogsResult?.error) {
        console.error('Error loading call logs:', callLogsResult?.error);
        setError(callLogsResult?.error);
      } else {
        setCallLogs(callLogsResult?.data || []);
      }

    } catch (err) {
      console.error('Failed to load calendar data:', err);
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced search with notes
  const handleSearchEvents = async () => {
    if (!searchQuery?.trim() || !user?.id) {
      loadCalendarData();
      return;
    }

    setLoading(true);
    try {
      const result = await calendarService?.searchEventsWithNotes?.(user?.id, searchQuery);
      if (result?.error) {
        setError(result?.error);
      } else {
        setCalendarEvents(result?.data || []);
      }
    } catch (err) {
      setError('Failed to search events');
    } finally {
      setLoading(false);
    }
  };

  // Quick note creation
  const handleQuickNote = async (noteData) => {
    try {
      const eventData = {
        title: noteData?.title || `Quick Note - ${format(noteData?.date, 'MMM d, yyyy')}`,
        description: noteData?.note,
        event_type: 'reminder',
        start_time: noteData?.date?.toISOString(),
        end_time: new Date(noteData?.date?.getTime() + 15 * 60000)?.toISOString(), // 15 minutes
        priority: 'medium',
        status: 'scheduled',
        user_id: user?.id
      };

      const result = await calendarService?.createCalendarEvent?.(eventData);
      if (result?.error) {
        setError(result?.error);
      } else {
        loadCalendarData();
        setShowQuickNoteModal(false);
      }
    } catch (err) {
      setError('Failed to create quick note');
    }
  };

  // Filter events with notes
  const filteredEvents = showNotesFilter 
    ? calendarEvents?.filter(event => event?.description?.trim())
    : calendarEvents;

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleEventSaved = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
    loadCalendarData();
  };

  const handleEventDeleted = () => {
    loadCalendarData();
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const getTodaysEvents = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return filteredEvents?.filter(event => 
      format(new Date(event?.start_time || event?.startTime || new Date()), 'yyyy-MM-dd') === today
    );
  };

  return (
    <PageLayout>
      {/* Enhanced Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Calendar & Scheduling</h1>
          <p className="text-muted-foreground">
            Schedule meetings, manage appointments, and add notes for your calendar activities
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          {/* Search Bar */}
          <div className="flex items-center space-x-2">
            <div className="relative">
              <Input
                type="text"
                placeholder="Search events and notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && handleSearchEvents()}
                className="w-64"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSearchEvents}
                iconName="Search"
                className="absolute right-1 top-1/2 transform -translate-y-1/2"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNotesFilter(!showNotesFilter)}
              iconName="Filter"
              className={showNotesFilter ? 'bg-primary/10' : ''}
            >
              {showNotesFilter ? 'Show All' : 'Notes Only'}
            </Button>
          </div>
          
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            onClick={() => {/* Export calendar */}}
          >
            Export Calendar
          </Button>
          <Button
            variant="outline"
            iconName="StickyNote"
            iconPosition="left"
            onClick={() => {
              setQuickNoteDate(selectedDate);
              setShowQuickNoteModal(true);
            }}
          >
            Quick Note
          </Button>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={handleCreateEvent}
          >
            Schedule Event
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-destructive" />
            <span className="text-destructive text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading calendar...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('calendar')}
                  className={`
                    flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm
                    ${activeTab === 'calendar' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'}
                  `}
                >
                  <Icon name="Calendar" size={16} />
                  <span>Calendar</span>
                  {filteredEvents?.filter(e => e?.description?.trim())?.length > 0 && (
                    <span className="bg-primary/20 text-primary text-xs px-1.5 py-0.5 rounded-full">
                      {filteredEvents?.filter(e => e?.description?.trim())?.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('calls')}
                  className={`
                    flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm
                    ${activeTab === 'calls' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'}
                  `}
                >
                  <Icon name="Phone" size={16} />
                  <span>Call Logs</span>
                  {callLogs?.filter(c => c?.call_notes?.trim())?.length > 0 && (
                    <span className="bg-primary/20 text-primary text-xs px-1.5 py-0.5 rounded-full">
                      {callLogs?.filter(c => c?.call_notes?.trim())?.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('assistant')}
                  className={`
                    flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm
                    ${activeTab === 'assistant' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'}
                  `}
                >
                  <Icon name="Bot" size={16} />
                  <span>Scheduling Assistant</span>
                </button>
                <button
                  onClick={() => setActiveTab('notes')}
                  className={`
                    flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm
                    ${activeTab === 'notes' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'}
                  `}
                >
                  <Icon name="StickyNote" size={16} />
                  <span>All Notes</span>
                </button>
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'calendar' && (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-3">
                  <CalendarView
                    events={filteredEvents}
                    selectedDate={selectedDate}
                    viewMode={viewMode}
                    onDateSelect={setSelectedDate}
                    onViewModeChange={setViewMode}
                    onEventSelect={(event) => {
                      setSelectedEvent(event);
                      setShowEventModal(true);
                    }}
                    onCreateEvent={handleCreateEvent}
                  />
                </div>
                
                <div className="space-y-6">
                  {/* Today's Events with Notes Preview */}
                  <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-foreground">Today's Events</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setQuickNoteDate(new Date());
                          setShowQuickNoteModal(true);
                        }}
                        iconName="Plus"
                        className="text-muted-foreground hover:text-foreground"
                      />
                    </div>
                    {getTodaysEvents()?.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No events today</p>
                    ) : (
                      <div className="space-y-2">
                        {getTodaysEvents()?.map(event => (
                          <div 
                            key={event?.id} 
                            className="p-2 bg-muted rounded text-sm cursor-pointer hover:bg-muted/80"
                            onClick={() => {
                              setSelectedEvent(event);
                              setShowEventModal(true);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-foreground">{event?.title || event?.name || 'Untitled Event'}</p>
                              {event?.description?.trim() && (
                                <Icon name="StickyNote" size={12} className="text-primary" />
                              )}
                            </div>
                            <p className="text-muted-foreground">
                              {format(new Date(event?.start_time || event?.startTime || new Date()), 'HH:mm')}
                            </p>
                            {event?.description?.trim() && (
                              <p className="text-xs text-muted-foreground mt-1 truncate bg-primary/10 rounded px-2 py-1">
                                Note: {event?.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Contact Notes Panel */}
                  {selectedContact && (
                    <ContactNotesPanel
                      contact={selectedContact}
                      userId={user?.id}
                      onClose={() => setSelectedContact(null)}
                    />
                  )}
                </div>
              </div>
            )}

            {activeTab === 'calls' && (
              <CallLogPanel
                callLogs={callLogs}
                onCallLogUpdate={loadCalendarData}
                userId={user?.id}
                onContactSelect={setSelectedContact}
              />
            )}

            {activeTab === 'assistant' && (
              <SchedulingAssistant
                onEventCreated={handleEventSaved}
                userId={user?.id}
              />
            )}

            {activeTab === 'notes' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">All Notes & Annotations</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setQuickNoteDate(new Date());
                      setShowQuickNoteModal(true);
                    }}
                    iconName="Plus"
                  >
                    Add Quick Note
                  </Button>
                </div>

                {/* Event Notes */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h4 className="font-medium text-foreground mb-4 flex items-center space-x-2">
                    <Icon name="Calendar" size={16} />
                    <span>Event Notes</span>
                  </h4>
                  <div className="space-y-3">
                    {filteredEvents?.filter(event => event?.description?.trim())?.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No event notes found</p>
                    ) : (
                      filteredEvents?.filter(event => event?.description?.trim())?.map(event => (
                        <div
                          key={event?.id}
                          onClick={() => {
                            setSelectedEvent(event);
                            setShowEventModal(true);
                          }}
                          className="p-4 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h5 className="font-medium text-foreground">{event?.title}</h5>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(event?.start_time), 'MMM d, yyyy HH:mm')}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground bg-muted/50 rounded p-2">
                            {event?.description}
                          </p>
                          <div className="flex items-center space-x-2 mt-2">
                            <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full capitalize">
                              {event?.event_type}
                            </span>
                            <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-full capitalize">
                              {event?.priority} priority
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Call Notes */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h4 className="font-medium text-foreground mb-4 flex items-center space-x-2">
                    <Icon name="Phone" size={16} />
                    <span>Call Notes</span>
                  </h4>
                  <div className="space-y-3">
                    {callLogs?.filter(call => call?.call_notes?.trim())?.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No call notes found</p>
                    ) : (
                      callLogs?.filter(call => call?.call_notes?.trim())?.slice(0, 10)?.map(call => (
                        <div
                          key={call?.id}
                          className="p-4 border border-border rounded-lg"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Icon name="Phone" size={14} className="text-muted-foreground" />
                              <span className="font-medium text-foreground">
                                {call?.contact?.name || call?.phone_number || 'Unknown Contact'}
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(call?.call_started_at || call?.created_at), 'MMM d, yyyy HH:mm')}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground bg-muted/50 rounded p-2">
                            {call?.call_notes}
                          </p>
                          <div className="flex items-center space-x-2 mt-2">
                            <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                              call?.outcome === 'successful' ? 'bg-success/10 text-success' :
                              call?.outcome === 'interested'? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                            }`}>
                              {call?.outcome || 'N/A'}
                            </span>
                            <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-full">
                              {call?.duration_seconds ? `${Math.round(call?.duration_seconds / 60)}min` : 'No duration'}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Quick Stats with Note Metrics */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Today's Events</p>
                  <p className="text-2xl font-bold text-foreground">
                    {getTodaysEvents()?.length || 0}
                  </p>
                </div>
                <Icon name="Calendar" size={24} className="text-primary" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Events with Notes</p>
                  <p className="text-2xl font-bold text-foreground">
                    {filteredEvents?.filter(e => e?.description?.trim())?.length || 0}
                  </p>
                </div>
                <Icon name="StickyNote" size={24} className="text-success" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Call Notes</p>
                  <p className="text-2xl font-bold text-foreground">
                    {callLogs?.filter(c => c?.call_notes?.trim())?.length || 0}
                  </p>
                </div>
                <Icon name="MessageSquare" size={24} className="text-warning" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Recent Calls</p>
                  <p className="text-2xl font-bold text-foreground">
                    {callLogs?.length || 0}
                  </p>
                </div>
                <Icon name="Phone" size={24} className="text-info" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                  <p className="text-2xl font-bold text-foreground">
                    {callLogs?.length > 0 
                      ? Math.round(
                          (callLogs?.filter(call => call?.outcome === 'successful' || call?.outcome === 'interested' || call?.status === 'completed')?.length / callLogs?.length) * 100
                        )
                      : 0
                    }%
                  </p>
                </div>
                <Icon name="TrendingUp" size={24} className="text-success" />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Event Modal */}
      {showEventModal && (
        <EventModal
          event={selectedEvent}
          isOpen={showEventModal}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onCancel={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onSave={handleEventSaved}
          onDelete={handleEventDeleted}
          userId={user?.id}
        />
      )}

      {/* Quick Note Modal */}
      {showQuickNoteModal && (
        <QuickNoteModal
          date={quickNoteDate}
          onSave={handleQuickNote}
          onCancel={() => setShowQuickNoteModal(false)}
        />
      )}
    </PageLayout>
  );
};

export default CalendarScheduling;